/**
 * RoomPickerDialog - Visual room selection for booking assignment
 * Shows rooms in a pictorial grid with color-coded status
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bed, Users, DoorOpen, CheckCircle2, XCircle, Loader2,
  Cigarette, CigaretteOff, Building2, Layers, Check,
  Hotel, Calendar, User
} from 'lucide-react';

// Status color mapping
const statusColors = {
  available: {
    bg: 'bg-emerald-100 hover:bg-emerald-200 border-emerald-400',
    text: 'text-emerald-700',
    label: 'Available'
  },
  occupied: {
    bg: 'bg-red-100 border-red-400 opacity-60 cursor-not-allowed',
    text: 'text-red-700',
    label: 'Occupied'
  },
  reserved: {
    bg: 'bg-blue-100 border-blue-400 opacity-60 cursor-not-allowed',
    text: 'text-blue-700',
    label: 'Reserved'
  },
  maintenance: {
    bg: 'bg-yellow-100 border-yellow-400 opacity-60 cursor-not-allowed',
    text: 'text-yellow-700',
    label: 'Maintenance'
  },
  cleaning: {
    bg: 'bg-orange-100 border-orange-400 opacity-60 cursor-not-allowed',
    text: 'text-orange-700',
    label: 'Cleaning'
  },
  blocked: {
    bg: 'bg-gray-100 border-gray-400 opacity-60 cursor-not-allowed',
    text: 'text-gray-700',
    label: 'Blocked'
  }
};

// Room Card Component
const RoomCard = ({ room, isSelected, isRecommended, onClick, disabled }) => {
  const status = room.status || 'available';
  const colors = statusColors[status] || statusColors.available;
  const isAvailable = status === 'available';
  
  return (
    <div 
      className={`
        relative p-3 rounded-xl border-2 transition-all duration-200
        ${colors.bg}
        ${isSelected ? 'ring-2 ring-amber-500 ring-offset-2 border-amber-500' : ''}
        ${isRecommended && isAvailable ? 'ring-2 ring-green-400 ring-offset-1' : ''}
        ${isAvailable && !disabled ? 'cursor-pointer hover:scale-105 hover:shadow-lg' : ''}
      `}
      onClick={() => isAvailable && !disabled && onClick(room)}
      data-testid={`room-picker-${room.room_number}`}
    >
      {/* Recommended badge */}
      {isRecommended && isAvailable && (
        <div className="absolute -top-2 -right-2">
          <Badge className="bg-green-500 text-white text-xs px-1.5 py-0.5">
            <Check className="h-3 w-3 mr-0.5" />
            Match
          </Badge>
        </div>
      )}
      
      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute -top-2 -left-2">
          <div className="h-6 w-6 rounded-full bg-amber-500 flex items-center justify-center">
            <Check className="h-4 w-4 text-white" />
          </div>
        </div>
      )}
      
      {/* Room number */}
      <div className="text-center">
        <p className={`font-bold text-xl ${colors.text}`}>{room.room_number}</p>
        
        {/* Room type */}
        <p className="text-xs text-gray-600 truncate mt-0.5">
          {room.room_type_name || room.room_type || 'Standard'}
        </p>
        
        {/* Bed type icons */}
        <div className="flex items-center justify-center gap-1 mt-1">
          <Bed className="h-3 w-3 text-gray-500" />
          <span className="text-xs text-gray-500">
            {room.bed_type_names?.join(', ') || room.bed_type_name || 'Standard'}
          </span>
        </div>
        
        {/* Smoking indicator */}
        <div className="flex items-center justify-center mt-1">
          {room.smoking_allowed ? (
            <Cigarette className="h-3 w-3 text-gray-400" title="Smoking Allowed" />
          ) : (
            <CigaretteOff className="h-3 w-3 text-green-500" title="Non-Smoking" />
          )}
        </div>
        
        {/* Status label for non-available rooms */}
        {!isAvailable && (
          <Badge className={`mt-1 text-xs ${colors.text} bg-white/50`}>
            {colors.label}
          </Badge>
        )}
        
        {/* Guest name for occupied rooms */}
        {room.current_guest_name && (
          <p className="text-xs text-gray-500 mt-1 truncate">
            {room.current_guest_name}
          </p>
        )}
      </div>
    </div>
  );
};

// Floor Section Component
const FloorSection = ({ floor, rooms, selectedRoom, recommendedRooms, onRoomSelect }) => {
  const sortedRooms = [...rooms].sort((a, b) => {
    const numA = parseInt(a.room_number) || 0;
    const numB = parseInt(b.room_number) || 0;
    return numA - numB;
  });
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-4 w-4 text-gray-500" />
        <h3 className="font-semibold text-gray-700">Floor {floor}</h3>
        <Badge variant="outline" className="ml-auto text-xs">
          {rooms.filter(r => r.status === 'available').length} available
        </Badge>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
        {sortedRooms.map(room => (
          <RoomCard
            key={room.id}
            room={room}
            isSelected={selectedRoom?.id === room.id}
            isRecommended={recommendedRooms.some(r => r.id === room.id)}
            onClick={onRoomSelect}
          />
        ))}
      </div>
    </div>
  );
};

export default function RoomPickerDialog({ 
  open, 
  onOpenChange, 
  booking, 
  onRoomAssigned 
}) {
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [recommendedRooms, setRecommendedRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'list'
  const [floorFilter, setFloorFilter] = useState('all');

  // Load available rooms when dialog opens
  useEffect(() => {
    if (open && booking) {
      loadRooms();
    }
  }, [open, booking]);

  const loadRooms = async () => {
    setLoading(true);
    setSelectedRoom(null);
    
    try {
      // Get all rooms and available rooms for the booking
      const [allRoomsRes, availableRes] = await Promise.all([
        api.get('/rooms'),
        api.get(`/rooms/available-for-checkin/${booking.id}`)
      ]);
      
      const allRooms = allRoomsRes.data || [];
      const { matching_rooms = [], all_available_rooms = [] } = availableRes.data || {};
      
      // Create a set of available room IDs
      const availableIds = new Set([
        ...matching_rooms.map(r => r.id),
        ...all_available_rooms.map(r => r.id)
      ]);
      
      // Mark rooms as available or unavailable
      const enrichedRooms = allRooms.map(room => ({
        ...room,
        status: availableIds.has(room.id) ? 'available' : (room.status || 'occupied')
      }));
      
      setRooms(enrichedRooms);
      setRecommendedRooms(matching_rooms);
    } catch (error) {
      console.error('Failed to load rooms:', error);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  // Handle room selection
  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
  };

  // Handle room assignment
  const handleAssign = async () => {
    if (!selectedRoom) {
      toast.error('Please select a room');
      return;
    }
    
    setAssigning(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        room_id: selectedRoom.id
      });
      toast.success(`Room ${selectedRoom.room_number} assigned successfully`);
      onOpenChange(false);
      if (onRoomAssigned) {
        onRoomAssigned();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign room');
    } finally {
      setAssigning(false);
    }
  };

  // Group rooms by floor
  const roomsByFloor = rooms.reduce((acc, room) => {
    const floor = room.floor || 1;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(room);
    return acc;
  }, {});

  const floors = Object.keys(roomsByFloor).sort((a, b) => parseInt(a) - parseInt(b));
  const filteredFloors = floorFilter === 'all' ? floors : floors.filter(f => f === floorFilter);

  // Stats
  const availableCount = rooms.filter(r => r.status === 'available').length;
  const occupiedCount = rooms.filter(r => r.status === 'occupied').length;
  const recommendedCount = recommendedRooms.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] bg-white" data-testid="room-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <Hotel className="h-5 w-5 text-amber-600" />
            Select Room for Booking
          </DialogTitle>
        </DialogHeader>
        
        {/* Booking Info */}
        {booking && (
          <div className="flex flex-wrap items-center gap-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-amber-600" />
              <span className="font-medium">{booking.guest_name || 'Guest'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Hotel className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">{booking.room_type_name || 'Any Room Type'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Bed className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">{booking.bed_type_name || 'Any Bed'}</span>
            </div>
            <div className="flex items-center gap-2">
              {booking.smoking_preference === 'smoking' ? (
                <><Cigarette className="h-4 w-4 text-gray-500" /><span className="text-sm text-gray-600">Smoking</span></>
              ) : booking.smoking_preference === 'non_smoking' ? (
                <><CigaretteOff className="h-4 w-4 text-green-500" /><span className="text-sm text-gray-600">Non-Smoking</span></>
              ) : (
                <span className="text-sm text-gray-500">No Preference</span>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">
                {new Date(booking.check_in || booking.check_in_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - 
                {new Date(booking.check_out || booking.check_out_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>
            </div>
          </div>
        )}

        {/* Stats & Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {availableCount} Available
            </Badge>
            <Badge className="bg-green-100 text-green-700 border-green-300">
              <Check className="h-3 w-3 mr-1" />
              {recommendedCount} Matching
            </Badge>
            <Badge className="bg-red-100 text-red-700 border-red-300">
              <XCircle className="h-3 w-3 mr-1" />
              {occupiedCount} Occupied
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={floorFilter} onValueChange={setFloorFilter}>
              <SelectTrigger className="w-[140px] border-gray-200">
                <Layers className="h-4 w-4 mr-2 text-gray-500" />
                <SelectValue placeholder="All Floors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Floors</SelectItem>
                {floors.map(floor => (
                  <SelectItem key={floor} value={floor}>Floor {floor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="bg-gray-100">
                <TabsTrigger value="visual" className="text-xs">
                  <DoorOpen className="h-3 w-3 mr-1" />
                  Visual
                </TabsTrigger>
                <TabsTrigger value="list" className="text-xs">
                  <Layers className="h-3 w-3 mr-1" />
                  List
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs pb-2 border-b">
          <span className="text-gray-500">Legend:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-emerald-100 border-2 border-emerald-400" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-400 ring-2 ring-green-400 ring-offset-1" />
            <span>Recommended</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-amber-100 border-2 border-amber-500 ring-2 ring-amber-500 ring-offset-1" />
            <span>Selected</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-400 opacity-60" />
            <span>Occupied</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-400 opacity-60" />
            <span>Reserved</span>
          </div>
        </div>

        {/* Room Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            <span className="ml-3 text-gray-500">Loading rooms...</span>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            {viewMode === 'visual' ? (
              <div className="space-y-4">
                {filteredFloors.map(floor => (
                  <FloorSection
                    key={floor}
                    floor={floor}
                    rooms={roomsByFloor[floor]}
                    selectedRoom={selectedRoom}
                    recommendedRooms={recommendedRooms}
                    onRoomSelect={handleRoomSelect}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {rooms.filter(r => r.status === 'available').map(room => (
                  <div
                    key={room.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedRoom?.id === room.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'}
                      ${recommendedRooms.some(r => r.id === room.id) ? 'ring-1 ring-green-400' : ''}
                    `}
                    onClick={() => handleRoomSelect(room)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <span className="font-bold text-amber-700">{room.room_number}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{room.room_type_name || room.room_type}</p>
                        <p className="text-sm text-gray-500">
                          Floor {room.floor} • {room.bed_type_name || 'Standard Bed'}
                          {room.smoking_allowed ? ' • Smoking' : ' • Non-Smoking'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {recommendedRooms.some(r => r.id === room.id) && (
                        <Badge className="bg-green-100 text-green-700">Recommended</Badge>
                      )}
                      {selectedRoom?.id === room.id && (
                        <Check className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        )}

        {/* Selected Room Info */}
        {selectedRoom && (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-amber-500 flex items-center justify-center">
                <span className="font-bold text-white text-lg">{selectedRoom.room_number}</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Room {selectedRoom.room_number} Selected</p>
                <p className="text-sm text-gray-600">
                  {selectedRoom.room_type_name || selectedRoom.room_type} • Floor {selectedRoom.floor}
                  {selectedRoom.bed_type_name ? ` • ${selectedRoom.bed_type_name}` : ''}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedRoom(null)}
              className="text-gray-500"
            >
              Clear
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign}
            disabled={!selectedRoom || assigning}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-room-selection"
          >
            {assigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Assign Room {selectedRoom?.room_number || ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
